import { ChefHat, Plus, ScanLine, UserPlus, Utensils } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  DataTable,
  ExportButton,
  FilterBar,
  FilterDivider,
  MetricCard,
  MetricRow,
  Pager,
  Select,
  SelectableChip,
  StatusPill,
  type DataTableColumn,
  RangePicker,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { useSmallScreen } from '../../lib/util/smallScreen';
import { useAnalyticsWindow } from '../analytics/parts';
import { formatDate } from '../../lib/format/dates';
import { formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { LocalePicker } from '../locales/LocalePicker';
import { listParam, pickParam } from '../profiles/people';
import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_STATUSES,
  CAMPAIGN_TYPES,
  type Campaign,
  type CampaignFilters,
  type PerformanceRow,
  type QRInventoryRow,
} from './api';
import { NewCampaignDialog } from './NewCampaignDialog';
import { useCampaignLibrary, useExportPerformance, usePerformance, useQRInventory } from './queries';
import {
  CHANNEL_LABEL,
  COLOR_LABEL,
  CopyLink,
  STATUS_LABEL,
  STATUS_TONE,
  TYPE_LABEL,
  langLabel,
  variantLabel,
} from './parts';

const TABS = ['performance', 'library', 'qr'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = { performance: 'Performance', library: 'Library', qr: 'QR codes' };

const pct = (v: number) => `${v.toFixed(1)}%`;

/**
 * QR Codes & Campaigns (kit `CampaignsScreen`; FE plan §7). Three tabs —
 * Performance (figures over a date window), Library (every campaign) and QR
 * codes (every printed code). Tab, filters and page live in the URL; a
 * campaign opens on its own page, `/campaigns/<id>`.
 */
export function CampaignsScreen() {
  const table = useTableUrlState();
  const navigate = useNavigate();
  const f = table.filters;
  const tab = pickParam(f.tab, TABS) ?? 'performance';
  const lock = useSession().locked('marketing.write');
  // Read-only on a phone: no New campaign (and no ?new=1 form).
  const small = useSmallScreen();
  // `?new=1` — the router may hand it back as a number. Ignored without write access.
  const creating = String(f.new ?? '') === '1' && !lock && !small;
  const perfWindow = usePerformanceWindow();

  // Drop `?new=1` from this entry before opening the campaign (setParams
  // replaces entries, so the list entry IS the form's), or Back from the
  // campaign reopens the form instead of returning to the list.
  const openCreated = async (id: string) => {
    await navigate({
      to: '.',
      replace: true,
      search: ((prev: Record<string, unknown>) => ({ ...prev, new: undefined })) as never,
    });
    await navigate({ to: `/campaigns/${id}` as never });
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Campaign views">
        {TABS.map((t) => (
          <SelectableChip
            key={t}
            role="tab"
            aria-selected={tab === t}
            active={tab === t}
            onClick={() => table.setParams({ tab: t === 'performance' ? undefined : t, page: undefined })}
          >
            {TAB_LABEL[t]}
          </SelectableChip>
        ))}
        {tab === 'performance' && (
          <>
            <FilterDivider />
            <RangePicker value={perfWindow.range} onChange={perfWindow.setRange} />
          </>
        )}
        {!small && (
          <div className="ml-auto">
            <Button size="sm" lockedReason={lock} onClick={() => table.setParams({ new: '1' })}>
              <Plus size={14} aria-hidden="true" /> New campaign
            </Button>
          </div>
        )}
      </div>

      {tab === 'performance' && <PerformanceTab />}
      {tab === 'library' && <LibraryTab />}
      {tab === 'qr' && <QrTab />}

      {creating && (
        <NewCampaignDialog
          onClose={() => table.setParams({ new: undefined })}
          onCreated={(id) => void openCreated(id)}
        />
      )}
    </div>
  );
}

/** Opens a campaign's page; Back returns here with the tab and filters intact. */
function useOpenCampaign() {
  const navigate = useNavigate();
  return (id: string) => void navigate({ to: `/campaigns/${id}` as never });
}

/** Filters shared by Performance and Library. */
function useCampaignFilters() {
  const table = useTableUrlState();
  const f = table.filters;
  return {
    table,
    type: pickParam(f.type, CAMPAIGN_TYPES),
    channel: pickParam(f.channel, CAMPAIGN_CHANNELS),
    status: pickParam(f.status, CAMPAIGN_STATUSES),
    localeIds: listParam(f.localeIds),
  };
}

function CampaignFilterBar({ extra }: { extra?: React.ReactNode }) {
  const { table, type, channel, status, localeIds } = useCampaignFilters();
  return (
    <FilterBar>
      <SelectableChip active={!type} onClick={() => table.setFilter('type', undefined)}>
        All
      </SelectableChip>
      {CAMPAIGN_TYPES.map((t) => (
        <SelectableChip key={t} active={type === t} onClick={() => table.setFilter('type', t)}>
          {TYPE_LABEL[t]}
        </SelectableChip>
      ))}
      <FilterDivider />
      <Select
        aria-label="Channel"
        surface="white"
        compact
        style={{ width: 150 }}
        placeholder="All channels"
        value={channel ?? ''}
        options={CAMPAIGN_CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABEL[c] }))}
        onChange={(e) => table.setFilter('channel', e.target.value || undefined)}
      />
      <Select
        aria-label="Status"
        surface="white"
        compact
        style={{ width: 140 }}
        placeholder="Any status"
        value={status ?? ''}
        options={CAMPAIGN_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
        onChange={(e) => table.setFilter('status', e.target.value || undefined)}
      />
      <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
      {extra}
    </FilterBar>
  );
}

// --- performance ------------------------------------------------------------------------------

function performanceColumns(): DataTableColumn<PerformanceRow>[] {
  return [
    {
      id: 'name',
      header: 'Campaign',
      width: '2fr',
      wrap: true,
      cell: (r) => (
        <span className="min-w-0">
          <span className="block truncate font-semibold">{r.name}</span>
          <span className="block font-mono text-xs text-muted">{r.slug}</span>
        </span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      width: '1.2fr',
      wrap: true,
      cell: (r) => (
        <span className="text-[13px] text-[rgba(42,22,9,.72)]">
          {TYPE_LABEL[r.type]}
          <span className="block text-xs text-muted">{CHANNEL_LABEL[r.channel]}</span>
        </span>
      ),
    },
    {
      id: 'locale',
      header: 'Locale',
      width: '1fr',
      cell: (r) => <span className="truncate text-muted">{r.localeName || '—'}</span>,
    },
    {
      id: 'eng',
      header: 'Engage',
      width: '76px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCount(r.engagements),
    },
    {
      id: 'dev',
      header: 'People',
      width: '72px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCount(r.uniqueDevices),
    },
    { id: 'scans', header: 'Scans', width: '68px', align: 'right', numeric: true, cell: (r) => formatCount(r.scans) },
    {
      id: 'signups',
      header: 'Signups',
      width: '76px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCount(r.signups),
    },
    {
      id: 'conv',
      header: 'Conv.',
      width: '66px',
      align: 'right',
      numeric: true,
      cell: (r) => pct(r.conversionRatePct),
    },
    {
      id: 'split',
      header: 'Chef / eater',
      width: '96px',
      cell: (r) => (
        <span className="tabular-nums text-[rgba(42,22,9,.72)]">
          {formatCount(r.chefsRecruited)} / {formatCount(r.eatersRecruited)}
        </span>
      ),
    },
    {
      id: 'dishes',
      header: 'Dishes',
      width: '70px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCount(r.dishesCreated),
    },
    {
      id: 'status',
      header: 'Status',
      width: '112px',
      cell: (r) => (
        <StatusPill tone={STATUS_TONE[r.status]} dot={r.status === 'active'}>
          {STATUS_LABEL[r.status]}
        </StatusPill>
      ),
    },
  ];
}

/** The Performance tab's window: the standard range control, 30d by default. */
function usePerformanceWindow() {
  return useAnalyticsWindow({ range: '30d' });
}

function PerformanceTab() {
  const openCampaign = useOpenCampaign();
  const { table, type, channel, status, localeIds } = useCampaignFilters();
  const w = usePerformanceWindow();
  const filters: CampaignFilters = {
    type,
    channel,
    status,
    localeIds,
    startDate: w.window.startDate,
    endDate: w.window.endDate,
  };
  const q = usePerformance({ ...filters, page: table.page, pageSize: table.pageSize });
  const exporter = useExportPerformance();
  const small = useSmallScreen();
  const t = q.data?.totals;
  const n = (v: number | undefined) => (v !== undefined ? formatCount(v) : q.isError ? '—' : '…');

  return (
    <>
      <MetricRow>
        <MetricCard
          label="Engagements"
          value={n(t?.engagements)}
          hint="every link open and scan"
          accent="var(--admin-chart-1)"
          icon={<ScanLine size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Signups"
          value={n(t?.signups)}
          hint={t ? `${pct(t.conversionRatePct)} of ${formatCount(t.uniqueDevices)} people reached` : undefined}
          accent="var(--admin-chart-2)"
          icon={<UserPlus size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Chefs recruited"
          value={n(t?.chefsRecruited)}
          hint={t ? `${formatCount(t.eatersRecruited)} eaters` : undefined}
          accent="var(--admin-chart-3)"
          icon={<ChefHat size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Dishes generated"
          value={n(t?.dishesCreated)}
          hint="by recruited chefs"
          accent="var(--admin-chart-4)"
          icon={<Utensils size={15} aria-hidden="true" />}
        />
      </MetricRow>

      <div>
        <CampaignFilterBar
          extra={
            // No bulk export on a phone.
            small ? undefined : (
              <div className="ml-auto">
                <ExportButton label="Bulk export" onExport={() => exporter.mutateAsync(filters)} />
              </div>
            )
          }
        />
        <DataTable
          label="Campaign performance"
          minWidth={1240}
          columns={performanceColumns()}
          rows={q.data?.items ?? []}
          getRowId={(r) => r.campaignId}
          onRowClick={(r) => openCampaign(r.campaignId)}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          sortDisabled="Sorting not available yet"
          emptyLabel="No campaigns match those filters."
        />
        <Pager
          page={table.page}
          pageSize={table.pageSize}
          total={q.data?.total ?? 0}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          noun="campaigns"
        />
      </div>
    </>
  );
}

// --- library ------------------------------------------------------------------------------

function libraryColumns(): DataTableColumn<Campaign>[] {
  return [
    {
      id: 'name',
      header: 'Campaign',
      width: '2fr',
      wrap: true,
      cell: (r) => (
        <span className="min-w-0">
          <span className="block truncate font-semibold">{r.name}</span>
          <span className="block font-mono text-xs text-muted">{r.slug}</span>
        </span>
      ),
    },
    { id: 'type', header: 'Type', width: '1.2fr', cell: (r) => TYPE_LABEL[r.type] },
    { id: 'channel', header: 'Channel', width: '110px', cell: (r) => CHANNEL_LABEL[r.channel] },
    {
      id: 'locale',
      header: 'Locale',
      width: '1fr',
      cell: (r) => <span className="truncate text-muted">{r.localeName || '—'}</span>,
    },
    {
      id: 'source',
      header: 'Source',
      width: '1fr',
      cell: (r) => <span className="truncate font-mono text-xs">{r.utmSource}</span>,
    },
    {
      id: 'variants',
      header: 'Variants',
      width: '80px',
      align: 'right',
      numeric: true,
      cell: (r) => (r.variants.length ? r.variants.filter((v) => !v.disabled).length : '—'),
    },
    {
      id: 'created',
      header: 'Created',
      width: '104px',
      cell: (r) => <span className="text-muted">{formatDate(r.createdAt)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '112px',
      cell: (r) => (
        <StatusPill tone={STATUS_TONE[r.status]} dot={r.status === 'active'}>
          {STATUS_LABEL[r.status]}
        </StatusPill>
      ),
    },
  ];
}

function LibraryTab() {
  const openCampaign = useOpenCampaign();
  const { table, type, channel, status, localeIds } = useCampaignFilters();
  const q = useCampaignLibrary({
    page: table.page,
    pageSize: table.pageSize,
    type,
    channel,
    status,
    // The library filters on one locale; the picker's last choice wins.
    localeId: localeIds[localeIds.length - 1],
  });
  return (
    <div>
      <CampaignFilterBar />
      <DataTable
        label="Campaign library"
        minWidth={1080}
        columns={libraryColumns()}
        rows={q.data?.items ?? []}
        getRowId={(r) => r.id}
        onRowClick={(r) => openCampaign(r.id)}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        sortDisabled="Sorting not available yet"
        emptyLabel="No campaigns yet."
      />
      <Pager
        page={table.page}
        pageSize={table.pageSize}
        total={q.data?.total ?? 0}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        noun="campaigns"
      />
    </div>
  );
}

// --- QR codes ------------------------------------------------------------------------------

function qrColumns(): DataTableColumn<QRInventoryRow>[] {
  return [
    {
      id: 'campaign',
      header: 'Campaign',
      width: '1.8fr',
      wrap: true,
      cell: (r) => (
        <span className="min-w-0">
          <span className="block truncate font-semibold">{r.campaignName || '—'}</span>
          <span className="block font-mono text-xs text-muted">{r.campaignSlug}</span>
        </span>
      ),
    },
    { id: 'variant', header: 'Variant', width: '110px', cell: (r) => variantLabel(r.placement) },
    { id: 'color', header: 'Color', width: '130px', cell: (r) => COLOR_LABEL[r.color] ?? (r.color || '—') },
    { id: 'lang', header: 'Language', width: '120px', cell: (r) => langLabel(r.lang) },
    {
      id: 'locale',
      header: 'Locale',
      width: '1fr',
      cell: (r) => <span className="truncate text-muted">{r.localeName || '—'}</span>,
    },
    { id: 'scans', header: 'Scans', width: '70px', align: 'right', numeric: true, cell: (r) => formatCount(r.scans) },
    {
      id: 'conv',
      header: 'Signups',
      width: '76px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCount(r.conversionCount),
    },
    {
      id: 'rate',
      header: 'Conv.',
      width: '66px',
      align: 'right',
      numeric: true,
      cell: (r) => pct(r.conversionRatePct),
    },
    {
      id: 'link',
      header: 'Link',
      width: '1.6fr',
      cell: (r) => (r.shortUrl ? <CopyLink url={r.shortUrl} label="QR link" /> : <span className="text-muted">—</span>),
    },
  ];
}

function QrTab() {
  const openCampaign = useOpenCampaign();
  const table = useTableUrlState();
  const q = useQRInventory({ page: table.page, pageSize: table.pageSize });
  return (
    <div>
      <DataTable
        label="QR codes"
        minWidth={1180}
        columns={qrColumns()}
        rows={q.data?.items ?? []}
        getRowId={(r) => r.qrCodeId}
        onRowClick={(r) => (r.campaignId ? openCampaign(r.campaignId) : undefined)}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No QR codes yet."
      />
      <Pager
        page={table.page}
        pageSize={table.pageSize}
        total={q.data?.total ?? 0}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        noun="codes"
      />
    </div>
  );
}
